// Small module-scope observable holding the undecisioned-exceptions
// count for the current caller. Two consumers:
//
//   - Sidebar's IconAlert item, which shows a pill badge with the
//     count when > 0 (drives the "attention" signal without needing
//     the user to open the page).
//   - The /exceptions route, which calls refetchExceptionsCount()
//     after every successful decide so the sidebar pill updates
//     immediately (no waiting for a router event / focus).
//
// Deliberately plain — no Zustand, no context, no TanStack Query.
// One store, one hook via useSyncExternalStore. Silent failure —
// the badge is a hint, not critical infra; a network hiccup should
// not throw or block the app.

import { useSyncExternalStore } from 'react';
import { supabase } from '../supabase';

type Listener = () => void;

let currentCount: number | null = null;
let inFlight = false;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

/**
 * Publish a known count to the store without a network round trip.
 * Called by the /exceptions route after each successful list fetch
 * so the sidebar badge updates immediately, without waiting for the
 * next focus/refetch cycle. Setting to null clears back to "unknown".
 */
export function publishExceptionsCount(next: number | null): void {
  currentCount = next;
  notify();
}

/**
 * Fetch the current undecisioned-exceptions count from the server
 * and notify subscribers. RLS scopes the underlying query to what
 * the caller can see. No-op while a previous fetch is in flight so
 * rapid consecutive calls (e.g. focus + auth-ready firing together)
 * coalesce into one round trip.
 */
export async function refetchExceptionsCount(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    const { data, error } = await supabase.rpc('list_exceptions', {});
    if (error) return; // silent — badge is a hint
    currentCount = (data ?? []).length;
    notify();
  } finally {
    inFlight = false;
  }
}

/**
 * React hook returning the current count. Null while unknown
 * (first fetch hasn't landed yet). Consumers should render nothing
 * when null so the sidebar doesn't flash a "0" during hydration.
 */
export function useExceptionsCount(): number | null {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => currentCount,
    () => currentCount,
  );
}

// ---------------------------------------------------------------------------
// Test helpers — kept in the same file (rather than a __tests__ shim)
// so behavior stays visible. Not exported from the module's public
// index; anyone importing them explicitly opts in.

/** Reset store state between tests. */
export function __resetExceptionsCountForTests(): void {
  currentCount = null;
  inFlight = false;
  listeners.clear();
}

/** Read the current count synchronously (no subscribe). */
export function __getExceptionsCountForTests(): number | null {
  return currentCount;
}

/** Bypass the RPC and set the count directly (for tests). */
export function __setExceptionsCountForTests(next: number | null): void {
  currentCount = next;
  notify();
}
